/**
 * Страница сообщений (чат врач-пациент).
 * Доступна обеим ролям.
 */

import React, { useEffect, useState, useRef } from 'react';
import { messagesAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

interface Conversation {
  user_id: number;
  name: string;
  role: string;
  patient_id?: number;
  last_message: { text: string; created_at: string } | null;
  unread_count: number;
}

interface Msg {
  id: number;
  sender_id: number;
  text: string;
  sender_name: string;
  created_at: string;
}

const MessagesPage: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeChat, setActiveChat] = useState<number | null>(null);
  const [activeName, setActiveName] = useState('');
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesAPI.getConversations().then((res) => setConversations(res.data.conversations));
  }, []);

  const openChat = async (userId: number, name: string) => {
    setActiveChat(userId);
    setActiveName(name);
    const res = await messagesAPI.getMessages(userId, { limit: 100 });
    setMessages(res.data.messages);
    // Обновляем непрочитанные
    setConversations((prev) =>
      prev.map((c) => c.user_id === userId ? { ...c, unread_count: 0 } : c)
    );
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleSend = async () => {
    if (!text.trim() || !activeChat) return;
    const res = await messagesAPI.sendMessage(activeChat, text);
    setMessages([...messages, res.data.message]);
    setText('');
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  return (
    <div>
      <h1 style={{ marginBottom: 16 }}>Сообщения</h1>

      <div style={styles.layout}>
        {/* Список собеседников */}
        <div style={styles.sidebar}>
          {conversations.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 14, padding: 16 }}>
              {user?.role === 'patient' ? 'Вам пока не назначен врач' : 'Нет привязанных пациентов'}
            </p>
          ) : (
            conversations.map((c) => (
              <div
                key={c.user_id}
                style={{
                  ...styles.convItem,
                  backgroundColor: activeChat === c.user_id ? 'var(--bg-info)' : 'var(--bg-card)',
                }}
                onClick={() => openChat(c.user_id, c.name)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: 14 }}>{c.name}</strong>
                  {c.unread_count > 0 && (
                    <span style={styles.badge}>{c.unread_count}</span>
                  )}
                </div>
                {c.last_message && (
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.last_message.text}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Окно чата */}
        <div style={styles.chatArea}>
          {!activeChat ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>
              Выберите собеседника
            </div>
          ) : (
            <>
              <div style={styles.chatHeader}>{activeName}</div>
              <div style={styles.messagesContainer}>
                {messages.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      ...styles.msgBubble,
                      alignSelf: m.sender_id === user?.id ? 'flex-end' : 'flex-start',
                      backgroundColor: m.sender_id === user?.id ? '#2563eb' : 'var(--bg-btn-secondary)',
                      color: m.sender_id === user?.id ? '#fff' : 'var(--text-primary)',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 14 }}>{m.text}</p>
                    <span style={{
                      fontSize: 11,
                      color: m.sender_id === user?.id ? 'rgba(255,255,255,0.7)' : '#9ca3af',
                      marginTop: 4, display: 'block',
                    }}>
                      {new Date(m.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div style={styles.inputBar}>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  style={styles.msgInput}
                  placeholder="Введите сообщение..."
                />
                <button onClick={handleSend} style={styles.sendBtn}>
                  Отправить
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  layout: {
    display: 'flex', gap: 16, height: 'calc(100vh - 160px)',
  },
  sidebar: {
    width: 280, backgroundColor: 'var(--bg-card)', borderRadius: 12,
    boxShadow: 'var(--shadow-card)', overflowY: 'auto',
  },
  convItem: {
    padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)',
  },
  badge: {
    backgroundColor: '#dc2626', color: '#fff', borderRadius: 10,
    padding: '2px 8px', fontSize: 12, fontWeight: 600,
  },
  chatArea: {
    flex: 1, backgroundColor: 'var(--bg-card)', borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    display: 'flex', flexDirection: 'column',
  },
  chatHeader: {
    padding: '14px 20px', borderBottom: '1px solid var(--border-light)',
    fontWeight: 600, fontSize: 16,
  },
  messagesContainer: {
    flex: 1, padding: 20, overflowY: 'auto',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  msgBubble: {
    maxWidth: '70%', padding: '10px 14px', borderRadius: 12,
  },
  inputBar: {
    padding: '12px 16px', borderTop: '1px solid var(--border-light)',
    display: 'flex', gap: 8,
  },
  msgInput: {
    flex: 1, padding: '10px 14px', border: '1px solid var(--border-color)',
    borderRadius: 8, fontSize: 14,
  },
  sendBtn: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    padding: '10px 20px', borderRadius: 8, cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
};

export default MessagesPage;
